import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type { FindAllActivitiesQueryResponse } from "../types/FindAllActivities"

type FindAllActivitiesClient = typeof client<
  FindAllActivitiesQueryResponse,
  Error,
  never
>
type FindAllActivities = {
  data: FindAllActivitiesQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<FindAllActivitiesClient>>
  client: {
    parameters: Partial<Parameters<FindAllActivitiesClient>[0]>
    return: Awaited<ReturnType<FindAllActivitiesClient>>
  }
}
export const findAllActivitiesQueryKey = () =>
  [{ url: "/v1/activity/find-all-activities" }] as const
export type FindAllActivitiesQueryKey = ReturnType<
  typeof findAllActivitiesQueryKey
>
export function findAllActivitiesQueryOptions<
  TData = FindAllActivities["response"],
  TQueryData = FindAllActivities["response"],
>(
  options: FindAllActivities["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindAllActivities["response"],
    FindAllActivities["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findAllActivitiesQueryKey()
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindAllActivities["data"],
        FindAllActivities["error"]
      >({
        method: "get",
        url: `/v1/activity/find-all-activities`,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find all activities
 * @link /v1/activity/find-all-activities
 */
export function useFindAllActivities<
  TData = FindAllActivities["response"],
  TQueryData = FindAllActivities["response"],
  TQueryKey extends QueryKey = FindAllActivitiesQueryKey,
>(
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindAllActivities["response"],
        FindAllActivities["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindAllActivities["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindAllActivities["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? findAllActivitiesQueryKey()
  const query = useQuery<
    FindAllActivities["data"],
    FindAllActivities["error"],
    TData,
    any
  >({
    ...findAllActivitiesQueryOptions<TData, TQueryData>(clientOptions),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindAllActivities["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
